import React, { useState, useCallback, useEffect } from 'react';
import api from '@/utils/api';
import { toastMessage } from '@/utils/toast-message';
import { RefreshCw, Download, PhoneCall, TrendingUp, IndianRupee, Trophy } from 'lucide-react';
import { rrGetAgents } from '@/actions/rr';
import { InfoTip, ReportNotes } from '@/pages/reports/_Components/InfoTip';

interface DailyRow {
  date: string;
  received: number;
  worked: number;
  connected: number;
  not_connected: number;
  orders: number;
  revenue: number;
  aov: number;
  pct_connected: number;
  top_agent: string | null;
  top_connects: number;
  bottom_agent: string | null;
  bottom_connects: number;
  delta: number;
}
interface Totals {
  received: number; worked: number; connected: number; not_connected: number;
  orders: number; revenue: number; aov: number; pct_connected: number;
}
interface NcRow { reason: string; count: number; }
interface CallStatusRow { status: string; count: number; pct: number; }
interface CallStatusMeta { total: number; answered: number; missed: number; answer_rate: number; }
interface Agent { user_id: number; name: string; }

// color per call_status value (answered/missed/initiated + fallback)
const CALL_STATUS_COLOR: Record<string, string> = {
  answered: 'bg-green-500',
  missed: 'bg-red-400',
  initiated: 'bg-amber-400',
};
const callStatusColor = (s: string) => CALL_STATUS_COLOR[s.toLowerCase()] || 'bg-gray-400';
const titleCase = (s: string) => s.replace(/(^|\s|_)\w/g, m => m.toUpperCase()).replace(/_/g, ' ');

const fmt = (n: number) => (n ?? 0).toLocaleString('en-IN');
const money = (n: number) => '₹' + Math.round(n ?? 0).toLocaleString('en-IN');

const isoDaysAgo = (d: number) => {
  const t = new Date(); t.setDate(t.getDate() - d);
  return t.toISOString().slice(0, 10);
};

// "00:00" .. "23:00" — whole-hour options for the time-of-day window (minutes not needed)
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

const StatCard = ({ icon, label, value, sub, accent, info }: {
  icon: React.ReactNode; label: string; value: string; sub?: string; accent?: string; info?: string;
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3">
    <div className={`p-2.5 rounded-lg ${accent || 'bg-brandColor/10 text-brandColor'}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
        {label}{info && <InfoTip text={info} />}
      </p>
      <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{sub}</p>}
    </div>
  </div>
);

// Column-header definitions for the per-day table (keyed by header label)
const COL_INFO: Record<string, string> = {
  Received: 'ABC work items: cart events that arrived this day (IST). Same count as the Performance Report.',
  Worked: 'Of the cart events that arrived this day, how many an agent has since updated a disposition on. Counted by the day the cart arrived, so it never exceeds Received.',
  Connected: 'Outbound calls placed in ABC work that day and answered with at least 10 seconds of talk. Same rule as the Performance Report.',
  '% Conn': 'Connected calls ÷ outbound dials for the day.',
  'Not Conn': 'Outbound dials that did not connect (missed, hung up early, under 10 seconds).',
  Orders: 'Valid orders placed this day that carry the ABC tag and are credited to an agent. Cancelled orders are left out.',
  AOV: 'Average order value = revenue ÷ orders for the day.',
  'Top Performer': 'Agent with the most connected calls on this day.',
  'Bottom Performer': 'Lowest agent — counts only agents who connected at least one call that day. Agents with zero connects are not shown.',
  'Δ': 'Top connects − Bottom connects (the spread between best and weakest connecting agent).',
};

const AbcDailyReport: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(isoDaysAgo(6));
  const [toDate, setToDate] = useState(today);
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [agentId, setAgentId] = useState<number | ''>('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [rows, setRows] = useState<DailyRow[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [nc, setNc] = useState<NcRow[]>([]);
  const [callStatus, setCallStatus] = useState<CallStatusRow[]>([]);
  const [callMeta, setCallMeta] = useState<CallStatusMeta | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    rrGetAgents()
      .then(res => setAgents(((res?.data as Agent[]) || [])))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const payload = {
      from_date: fromDate, to_date: toDate,
      from_time: fromTime || undefined, to_time: toTime || undefined,
      agent_user_id: agentId || undefined,
    };
    try {
      const [res, csRes] = await Promise.all([
        api.req({ url: '/reports/abc/daily', method: api.Method.POST, data: payload }) as unknown as
          Promise<{ data: DailyRow[]; totals: Totals; nc_breakdown: NcRow[] }>,
        api.req({ url: '/reports/abc/call-status', method: api.Method.POST, data: payload }) as unknown as
          Promise<{ data: CallStatusRow[] } & CallStatusMeta>,
      ]);
      setRows(res.data || []);
      setTotals(res.totals || null);
      setNc(res.nc_breakdown || []);
      setCallStatus(csRes.data || []);
      setCallMeta({ total: csRes.total, answered: csRes.answered, missed: csRes.missed, answer_rate: csRes.answer_rate });
    } catch {
      toastMessage('Failed to load ABC daily report', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, fromTime, toTime, agentId]);

  useEffect(() => { fetchData(); /* initial load */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCsv = () => {
    const head = ['Date', 'Received', 'Worked', 'Connected', '% Connected', 'Not Connected', 'Orders', 'AOV', 'Top Agent', 'Top Connects', 'Bottom Agent', 'Bottom Connects', 'Delta'];
    const lines = rows.map(r => [
      r.date, r.received, r.worked, r.connected, r.pct_connected, r.not_connected,
      r.orders, r.aov, r.top_agent || '', r.top_connects, r.bottom_agent || '', r.bottom_connects, r.delta,
    ].join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const timeSuffix = (fromTime || toTime) ? `_${fromTime || '00:00'}-${toTime || '23:59'}`.replace(/:/g, '') : '';
    a.download = `abc-daily_${fromDate}_${toDate}${timeSuffix}.csv`;
    a.click();
  };

  const ncMax = Math.max(1, ...nc.map(n => n.count));

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header / filters */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-brandColor p-2 rounded-lg"><PhoneCall className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">ABC Daily Report</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Abandoned-cart calling performance, day by day</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={fromDate} max={toDate} onChange={e => setFromDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor" />
            <input type="date" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor" />
            <div className="flex items-center gap-1 pl-1">
              <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                Hours <InfoTip text="Optional IST hour-of-day window applied across the range — leave blank for the full 24h. One-sided works too (e.g. From 09:00 = 09:00 to end of day). To 18:00 means up to 18:00 sharp." />
              </span>
              <select value={fromTime} onChange={e => setFromTime(e.target.value)} aria-label="From hour"
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor">
                <option value="">From</option>
                {HOURS.map(h => <option key={h} value={h} disabled={!!toTime && h > toTime}>{h}</option>)}
              </select>
              <span className="text-xs text-gray-400">–</span>
              <select value={toTime} onChange={e => setToTime(e.target.value)} aria-label="To hour"
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor">
                <option value="">To</option>
                {HOURS.map(h => <option key={h} value={h} disabled={!!fromTime && h < fromTime}>{h}</option>)}
              </select>
              {(fromTime || toTime) && (
                <button onClick={() => { setFromTime(''); setToTime(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 px-1" title="Clear hour window">✕</button>
              )}
            </div>
            <select value={agentId} onChange={e => setAgentId(e.target.value ? Number(e.target.value) : '')}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor max-w-[180px]">
              <option value="">All agents</option>
              {agents.map(a => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
            </select>
            <button onClick={fetchData} disabled={loading}
              className="flex items-center gap-2 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /><span>{loading ? 'Loading…' : 'Run'}</span>
            </button>
            <button onClick={downloadCsv} disabled={!rows.length}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              <Download className="h-4 w-4" /><span>CSV</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* KPI cards */}
        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={<PhoneCall className="h-5 w-5" />} label="Leads Received" value={fmt(totals.received)} sub={`${fmt(totals.worked)} worked`} info="ABC cart events that arrived in this range. 'Worked' is how many of those an agent has since updated a disposition on." />
            <StatCard icon={<TrendingUp className="h-5 w-5" />} label="% Connected" value={`${totals.pct_connected}%`} sub={`${fmt(totals.connected)} connected`} accent="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" info="Connected calls ÷ outbound dials across the range. Connected = answered with at least 10 seconds of talk. Same number as the Performance Report's Calls tab for ABC." />
            <StatCard icon={<IndianRupee className="h-5 w-5" />} label="AOV" value={money(totals.aov)} sub={`${fmt(totals.orders)} orders`} accent="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400" info="Average order value = valid revenue ÷ valid orders, ABC-tagged and credited to an agent, in the range." />
            <StatCard icon={<PhoneCall className="h-5 w-5" />} label="Not Connected" value={fmt(totals.not_connected)} sub="in range" accent="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" info="Outbound dials that did not connect in the range. The reason breakdown below is by disposition, a separate count of leads." />
          </div>
        )}

        {/* Call-status + Not-connected breakdowns */}
        {(callStatus.length > 0 || nc.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Call status from call logs */}
            {callStatus.length > 0 && callMeta && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                    Call Status
                    <InfoTip text="Counts call attempts (not leads) — an ABC lead is typically dialled 2–3 times, so this total is much higher than 'Worked' in the table. Outbound call logs for ABC leads in this range/window, grouped by call_status. 'Initiated' are Tata-Tele calls (that provider does not report answered/missed). Answer rate = answered ÷ total calls." />
                  </h2>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {fmt(callMeta.total)} calls · <span className="text-green-600 dark:text-green-400 font-medium">{callMeta.answer_rate}% answered</span>
                  </span>
                </div>
                <div className="space-y-2">
                  {callStatus.map(r => (
                    <div key={r.status} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 dark:text-gray-300 w-24 truncate">{titleCase(r.status)}</span>
                      <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded h-4 overflow-hidden">
                        <div className={`h-full ${callStatusColor(r.status)}`} style={{ width: `${r.pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-200 w-20 text-right">{fmt(r.count)} <span className="text-gray-400">({r.pct}%)</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Not-connected breakdown by reason / attempt */}
            {nc.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Not-Connected — by reason / attempt</h2>
                <div className="space-y-2">
                  {nc.map(r => (
                    <div key={r.reason} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 dark:text-gray-300 w-32 truncate">{r.reason}</span>
                      <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded h-4 overflow-hidden">
                        <div className="h-full bg-red-400/70" style={{ width: `${(r.count / ncMax) * 100}%` }} />
                      </div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-200 w-12 text-right">{fmt(r.count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per-day table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400">
              <tr>
                {['Date', 'Received', 'Worked', 'Connected', '% Conn', 'Not Conn', 'Orders', 'AOV', 'Top Performer', 'Bottom Performer', 'Δ'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-medium whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">{h}{COL_INFO[h] && <InfoTip text={COL_INFO[h]} />}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map(r => (
                <tr key={r.date} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmt(r.received)}</td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmt(r.worked)}</td>
                  <td className="px-3 py-2.5 text-green-600 dark:text-green-400 font-medium">{fmt(r.connected)}</td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{r.pct_connected}%</td>
                  <td className="px-3 py-2.5 text-red-500">{fmt(r.not_connected)}</td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmt(r.orders)}</td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{r.orders ? money(r.aov) : '—'}</td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {r.top_agent ? <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3 text-amber-500" />{r.top_agent} <span className="text-gray-400">({r.top_connects})</span></span> : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{r.bottom_agent ? `${r.bottom_agent} (${r.bottom_connects})` : '—'}</td>
                  <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300 font-medium">{r.delta}</td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-gray-400">No data for this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* How to read this report */}
        <ReportNotes items={[
          { term: 'Received', def: 'ABC cart events that arrived in the range: daily inflow, the same count as the Performance Report.' },
          { term: 'Worked', def: 'Of the cart events that arrived in the range, those an agent has since updated a disposition on. Counted by arrival day, so it never exceeds Received.' },
          { term: '% Connected', def: 'Connected calls ÷ outbound dials. Connected = an answered outbound call with at least 10 seconds of talk. Inbound calls, Engati calls and dialer clicks with no outcome are not counted.' },
          { term: 'Orders & AOV', def: 'Valid orders carrying the ABC tag and credited to an agent (cancelled and voided orders are left out). AOV = revenue ÷ orders, shown as "—" on days with no orders.' },
          { term: 'Top / Bottom Performer', def: 'Agents with the most / fewest connected calls that day. Bottom counts only agents with at least one connect — zero-connect agents are not shown, so Δ can understate the true spread.' },
          { term: 'Δ (delta)', def: 'Top connects − Bottom connects: the gap between the best and weakest connecting agent for the day.' },
          { term: 'Today’s row', def: 'The current day is partial — counts grow through the day, so early-morning % values (e.g. 100% off a single connect) are not representative.' },
          { term: 'Time window', def: 'Optional From/To time (IST) restricts every metric — Received, Worked, calls, performers and Orders — to events whose time-of-day falls in that window, on each day of the range. Leave both blank for the full 24h. The not-connected reason breakdown is by disposition time.' },
        ]} />
      </div>
    </div>
  );
};

export default AbcDailyReport;
